import React, {
  useState,
} from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Chip,
  alpha,
  Radio,
  RadioGroup,
  FormControl,
  Alert,
} from '@mui/material';
import {
  CheckCircle as SelectedIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  STATIC_GAME_IMAGES,
  getCategoryColor,
} from '../../utils/gameImages';

/**
 * StaticImagePicker Component
 * Visual selector for choosing static game images from public/games/ folder
 * Images shown here are guaranteed to persist across deployments
 */
function StaticImagePicker({ selectedImage, onImageSelect, helperText }) {
    const [hoveredImage, setHoveredImage] = useState(null);

    const handleSelect = (imagePath) => {
        onImageSelect(imagePath);
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ImageIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Select Static Game Image
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {helperText || 'Choose from available images that persist across deployments'}
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                These images are stored in the frontend and will <strong>never disappear</strong> on redeployment.
                To add new images, upload them to the <code>public/games/</code> folder.
            </Alert>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                    value={selectedImage || ''}
                    onChange={(e) => handleSelect(e.target.value)}
                >
                    <Grid container spacing={2}>
                        {STATIC_GAME_IMAGES.map((image) => {
                            const isSelected = selectedImage === image.path;
                            const isHovered = hoveredImage === image.path;

                            return (
                                <Grid item xs={6} sm={4} md={3} key={image.path}>
                                    <Card
                                        sx={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            border: 2,
                                            borderColor: isSelected ? 'primary.main' : 'transparent',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: 4,
                                                borderColor: 'primary.light'
                                            },
                                            ...(isSelected && {
                                                boxShadow: 4,
                                                bgcolor: alpha('#C9A227', 0.05)
                                            })
                                        }}
                                        onMouseEnter={() => setHoveredImage(image.path)}
                                        onMouseLeave={() => setHoveredImage(null)}
                                        onClick={() => handleSelect(image.path)}
                                    >
                                        <Radio
                                            value={image.path}
                                            sx={{ display: 'none' }}
                                            checked={isSelected}
                                        />
                                        
                                        {/* Image */}
                                        <Box sx={{ position: 'relative', height: 120 }}>
                                            <CardMedia
                                                component="img"
                                                height="120"
                                                image={image.path}
                                                alt={image.name}
                                                sx={{
                                                    objectFit: 'cover',
                                                    filter: isSelected ? 'brightness(1.1)' : 'brightness(0.95)'
                                                }}
                                            />
                                            
                                            {/* Selection Overlay */}
                                            {isSelected && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        bgcolor: 'primary.main',
                                                        borderRadius: '50%',
                                                        p: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <SelectedIcon sx={{ color: 'white', fontSize: 20 }} />
                                                </Box>
                                            )}
                                            
                                            {/* Hover Overlay */}
                                            {isHovered && !isSelected && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        bgcolor: alpha('#000', 0.3),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                                        Click to Select
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Info */}
                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: isSelected ? 700 : 600,
                                                    fontSize: '0.8rem',
                                                    mb: 0.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {image.name}
                                            </Typography>
                                            
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={image.category}
                                                    size="small"
                                                    color={getCategoryColor(image.category)}
                                                    sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                                />
                                                <Chip
                                                    label={image.filename}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: '0.6rem' }}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </RadioGroup>
            </FormControl>

            {/* Selected Image Info */}
            {selectedImage && (
                <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#C9A227', 0.1), borderRadius: 2, border: 1, borderColor: 'primary.main' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ✓ Selected Image:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <code>{selectedImage}</code>
                    </Typography>
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                        This image will persist across all deployments
                    </Typography>
                </Box>
            )}

            {/* No Selection Info */}
            {!selectedImage && (
                <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#FFA726', 0.1), borderRadius: 2, border: 1, borderColor: 'warning.main' }}>
                    <Typography variant="body2" color="text.secondary">
                        No static image selected. You can still use file upload or URL below, but those may not persist across deployments.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default StaticImagePicker;

